/* eslint-disable prefer-destructuring */
/* eslint-enable es/no-destructuring */
import axios from 'axios';
import {
  getEnvironment,
  parseReviewEnvironmentHost,
  getApiHost,
  ENVIRONMENTS,
  HOSTS_PER_ENV,
} from '@hotjar/common';

const reviewEnvUrlSuffix = parseReviewEnvironmentHost()?.reviewUrlSuffix;

// Settings for the API and client script URLs
const SETTINGS = Object.freeze({
  [ENVIRONMENTS.LIVE]: {
    apiHost: HOSTS_PER_ENV[ENVIRONMENTS.LIVE].SURVEYS,
    clientScriptHost: 'static.hotjar.com',
  },
  [ENVIRONMENTS.REVIEW_WEBAPP]: {
    apiHost: 'surveys-integration.live.eks.hotjar.com',
    clientScriptHost: `static-integration.live.eks.hotjar.com`,
  },
  [ENVIRONMENTS.REVIEW]: {
    apiHost: 'surveys-integration.live.eks.hotjar.com',
    clientScriptHost: `static-${reviewEnvUrlSuffix}`,
  },
  [ENVIRONMENTS.STAGING]: {
    apiHost: HOSTS_PER_ENV[ENVIRONMENTS.STAGING].SURVEYS,
    clientScriptHost: 'static-staging.hotjar.com',
  },
  [ENVIRONMENTS.DEV]: {
    apiHost: getApiHost(false),
    clientScriptHost: 'static.local.hotjar.com:8443',
  },
  [ENVIRONMENTS.DEV_OLD]: {
    apiHost: getApiHost(false),
    clientScriptHost: 'static.local.hotjar.com:8443',
  },
});

// Key names match keys in hjSettings as expected by the client script. Don't change them!
const SCRIPT_SETTINGS = Object.freeze({
  // We don't want to override any settings on live
  [ENVIRONMENTS.LIVE]: {},
  [ENVIRONMENTS.REVIEW_WEBAPP]: {
    host: `in-integration.live.eks.hotjar.com`,
    identifyEndpoint: 'https://identify.ew1-integration-1.hotjarians.net',
    insightsHost: HOSTS_PER_ENV[ENVIRONMENTS.REVIEW_WEBAPP].INSIGHTS,
    insightsApiHost: HOSTS_PER_ENV[ENVIRONMENTS.REVIEW_WEBAPP].INSIGHTS,
    staticHost: `static-integration.live.eks.hotjar.com`,
    varsHost: `vars-integration.live.eks.hotjar.com`,
    viewCounterEndpoint: 'https://vc-staging.hotjar.io/sessions',
    viewCounterHitPercentage: 1.0,
    surveyImpressionsEndpoint: 'https://surveystats-staging.hotjar.io/hit',
    wsHost: `ws1-integration.live.eks.hotjar.com`,
    metricsUrl: 'https://pacman-metrics-integration.live.eks.hotjar.com/',
    askUrl: 'https://in-ask-backend-integration.live.eks.hotjar.com/',
  },
  [ENVIRONMENTS.REVIEW]: {
    host: `in-${reviewEnvUrlSuffix}`,
    identifyEndpoint: 'https://identify.ew1-integration-1.hotjarians.net',
    insightsHost: HOSTS_PER_ENV[ENVIRONMENTS.REVIEW].INSIGHTS,
    insightsApiHost: HOSTS_PER_ENV[ENVIRONMENTS.REVIEW].INSIGHTS,
    staticHost: `static-${reviewEnvUrlSuffix}`,
    varsHost: `vars-${reviewEnvUrlSuffix}`,
    viewCounterEndpoint: 'https://vc-staging.hotjar.io/sessions',
    viewCounterHitPercentage: 1.0,
    surveyImpressionsEndpoint: 'https://surveystats-staging.hotjar.io/hit',
    wsHost: `ws1-${reviewEnvUrlSuffix}`,
    metricsUrl: 'https://pacman-metrics-integration.live.eks.hotjar.com/',
    askUrl: 'https://in-ask-backend-integration.live.eks.hotjar.com/',
  },
  [ENVIRONMENTS.STAGING]: {
    host: 'in-staging.hotjar.com',
    identifyEndpoint: 'https://identify.ew1-integration-1.hotjarians.net',
    insightsHost: HOSTS_PER_ENV[ENVIRONMENTS.STAGING].INSIGHTS,
    insightsApiHost: HOSTS_PER_ENV[ENVIRONMENTS.STAGING].INSIGHTS,
    staticHost: 'static-staging.hotjar.com',
    varsHost: 'vars-staging.hotjar.com',
    viewCounterEndpoint: 'https://vc-staging.hotjar.io/sessions',
    viewCounterHitPercentage: 1.0,
    surveyImpressionsEndpoint: 'https://surveystats-staging.hotjar.io/hit',
    wsHost: 'ws1-staging.hotjar.com',
    metricsUrl: 'https://pacman-metrics-integration.live.eks.hotjar.com/',
    askUrl: 'https://in-ask-backend-integration.live.eks.hotjar.com/',
  },
  [ENVIRONMENTS.DEV]: {
    host: 'in.local.hotjar.com:8443',
    identifyEndpoint: 'http://localhost:8000',
    insightsHost: HOSTS_PER_ENV[ENVIRONMENTS.DEV].INSIGHTS,
    insightsApiHost: HOSTS_PER_ENV[ENVIRONMENTS.DEV].INSIGHTS,
    staticHost: 'static.local.hotjar.com:8443',
    varsHost: 'vars.local.hotjar.com:9443',
    viewCounterEndpoint: null, // No view counting
    surveyImpressionsEndpoint: null, // No impression counting
    wsHost: 'ws.local.hotjar.com:8443',
    metricsUrl: 'https://pacman-metrics-integration.live.eks.hotjar.com/',
    // TODO: Set this to the proper local host for Ask service when it's defined
    askUrl: HOSTS_PER_ENV[ENVIRONMENTS.DEV].INSIGHTS,
  },
  [ENVIRONMENTS.DEV_OLD]: {
    host: 'in.local.hotjar.com:8443',
    identifyEndpoint: 'http://localhost:8000',
    insightsHost: HOSTS_PER_ENV[ENVIRONMENTS.DEV_OLD].INSIGHTS,
    insightsApiHost: HOSTS_PER_ENV[ENVIRONMENTS.DEV_OLD].INSIGHTS,
    staticHost: 'static.local.hotjar.com:8443',
    varsHost: 'vars.local.hotjar.com:9443',
    viewCounterEndpoint: null, // No view counting
    surveyImpressionsEndpoint: null, // No impression counting
    wsHost: 'ws.local.hotjar.com:8443',
    metricsUrl: 'https://pacman-metrics-integration.live.eks.hotjar.com/',
  },
});

function renderClosedSurvey(container, showBranding = true) {
  // eslint-disable-next-line no-param-reassign
  container.innerHTML = '';
  const errorContainer = document.createElement('div');
  errorContainer.classList.add('error-container');

  const heading = document.createElement('h3');
  const headingText = document.createTextNode('Nothing to see here');
  heading.appendChild(headingText);

  const paragraph = document.createElement('p');
  const paragraphText = document.createTextNode(
    'It looks like this survey stopped collecting responses or was removed. Maybe both!',
  );
  paragraph.appendChild(paragraphText);

  container.classList.add('_hj-survey-404');
  errorContainer.appendChild(heading);
  errorContainer.appendChild(paragraph);

  if (showBranding) {
    const brandingLink = document.createElement('a');
    const brandingLinkText = document.createTextNode('Try Surveys now');
    brandingLink.appendChild(brandingLinkText);
    brandingLink.classList.add('btn');
    brandingLink.href =
      'https://www.hotjar.com/feedback-surveys/?utm_source=external_survey_closed';
    brandingLink.target = '_blank';

    errorContainer.appendChild(brandingLink);
    container.classList.add('_hj-show-brand');
  }

  container.appendChild(errorContainer);
}

export function renderCallback(props) {
  document.body.style.backgroundColor = props.background;
}

function postMessageToBridge(message) {
  const handler = window.CSSurveysJavascriptBridge?.postMessage;
  if (typeof handler !== 'function') return;
  handler(JSON.stringify({ message }));
}

function registerSurveyBridgeHandlers() {
  window.addEventListener('hotjar-survey-event', (e) => {
    const messages = {
      surveyClosed: 'close',
      surveyDisplayed: 'open',
      surveyCompleted: 'survey-finished',
      surveyCompletedError: 'survey-finished',
    };

    const message = messages[e.detail.event];
    if (!message) return;
    postMessageToBridge(message);
  });
}

async function getSiteInfo(apiHost, providedId) {
  const siteIdParam = new URLSearchParams(window.location.search).get('siteId');
  if (siteIdParam !== null) return { site_id: Number(siteIdParam), enabled: true };
  const { data: siteInfo } = await axios.get(
    `https://${apiHost}/api/ask/v1/client/sites/offsite-survey-info/${providedId}`,
  );
  return siteInfo;
}

export async function renderSurvey() {
  const pathname = document.location.pathname;
  const environment = getEnvironment('SURVEYS');
  const environmentSettings = SETTINGS[environment];
  const scriptSettings = SCRIPT_SETTINGS[environment];

  const providedId = pathname.replace('/', '');
  const embedContainer = document.getElementsByClassName('_hj-survey-embed-container')[0];

  document.addEventListener(
    'hj-embedded-survey-mismatch',
    () => {
      renderClosedSurvey(embedContainer);
    },
    false,
  );

  registerSurveyBridgeHandlers();

  // todo: at this point we don't have access to feature flags for site
  // change this host to refer to ask.hotjar.io entry once split from monolith is done
  // and usages of 'ask.separate_service' are removed
  try {
    const siteInfo = await getSiteInfo(environmentSettings.apiHost, providedId);

    if (siteInfo.enabled === false) {
      renderClosedSurvey(embedContainer, siteInfo.effective_show_branding);
      postMessageToBridge('disabled');
      return;
    }

    const siteId = siteInfo.site_id;

    embedContainer.setAttribute('data-survey-id', providedId);

    // Hotjar Tracking code for shared surveys.
    /* eslint-disable no-param-reassign, no-underscore-dangle */
    const loadScript = (h, o, t, j, a, r) => {
      h.hj =
        h.hj ||
        // Don't change this to an arrow function. It minifies better this way.
        // eslint-disable-next-line func-names
        function () {
          // eslint-disable-next-line prefer-rest-params
          (h.hj.q = h.hj.q || []).push(arguments);
        };

      h._hjSettings = {
        hjid: siteId,
        hjsv: 6,
        ...scriptSettings,
      };

      const headElements = o.getElementsByTagName('head');
      a = headElements[0];
      r = o.createElement('script');
      r.async = 1;
      r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
      a.appendChild(r);
    };
    /* eslint-enable no-param-reassign, no-underscore-dangle */

    loadScript(
      window,
      document,
      `https://${environmentSettings.clientScriptHost}/c/hotjar-`,
      '.js?sv=',
    );
  } catch (error) {
    renderClosedSurvey(embedContainer);
  }
}
